import { type CSSProperties, type FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import StarIcon from '@assets/img/icons/star.svg?react';
import cn from 'classnames';

import { type LeagueRewardType } from '@/common/types/league';
import {
  StringMain,
  StringSmall,
  TextSmall,
} from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import { rankMedalIcons } from '../config';
import { RewardItem } from '../RewardItem/RewardItem';

import { PositionItemRewards } from './PositionItemRewards';

import css from './styles.module.scss';

interface TopItemRequirenmentsProps {
  minTarget: number;
  item: string;
  rewards: LeagueRewardType['rewards'];
  className?: string;
  myInfo?: {
    avatar?: string;
    userName?: string;
    level?: number;
  };
  style?: CSSProperties;
}

const TopItemRequirenments: FC<TopItemRequirenmentsProps> = ({
  item,
  minTarget,
  rewards,
  myInfo,
  className,
  style,
}) => {
  const { t } = useTranslation();
  const [positionRewardIsOpen, setPositionRewardIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [top, setTop] = useState(0);

  const setTopOffset = (): void => {
    setTop(wrapperRef.current?.offsetTop || 0);
  };

  return (
    <div
      ref={wrapperRef}
      style={style}
      className={cn(
        'w-full p-[4px] h-[3.125rem] rounded-lg bg-white/10 backdrop-blur-3xl',
        myInfo ? css['topReqItem--my'] : css.topReqItem,
        className,
      )}
    >
      <div className="flex flex-col gap-[2px] justify-center items-center mr-[4px]">
        <span className="text-[8px] font-bold text-white/40">{t('TOP')}</span>
        <StringMain weight="bold" className={cn('!text-[10px]', css.position)}>
          {`${item}%`}
        </StringMain>
      </div>
      {myInfo ? (
        <div
          style={{ backgroundImage: `url("${myInfo.avatar}")` }}
          className="size-9 rounded-full overflow-hidden bg-center bg-cover"
        ></div>
      ) : (
        <Picture
          img={rankMedalIcons[item as keyof typeof rankMedalIcons].png}
          webp={rankMedalIcons[item as keyof typeof rankMedalIcons].webp}
          className="w-9 h-auto"
          alt={`rank-${item}`}
        />
      )}
      {myInfo ? (
        <div className="flex flex-col justify-between items-start mr-2 overflow-hidden">
          <TextSmall className="!leading-1.2 !text-white overflow-hidden text-ellipsis w-full whitespace-nowrap">
            {myInfo.userName}
          </TextSmall>
          <div className="flex gap-[4px] items-center mr-8">
            <StarIcon className="size-5 shrink-0 fill-gold" />
            <StringSmall weight="bold">{minTarget}</StringSmall>
          </div>
          {/* <div className={cn(css.level, css['level--default'])}>
            <span className="font-medium text-[10px] leading-none text-white">
              {`${t('Level')} ${myInfo.level}`}
            </span>
          </div> */}
        </div>
      ) : (
        <div className="flex flex-col items-start justify-center gap-[2px] mr-2">
          <TextSmall weight="regular" className="!text-white/60 !leading-1.2">
            {t('Minimum target')}
          </TextSmall>
          <div className="flex gap-[4px] items-center mr-8">
            <StarIcon className="size-5 shrink-0 fill-gold" />
            <StringSmall weight="bold">{minTarget}</StringSmall>
          </div>
        </div>
      )}
      <PositionItemRewards
        kind="default"
        rewards={rewards}
        setPositionRewardIsOpen={setPositionRewardIsOpen}
        setTopOffset={setTopOffset}
      />
      {/* <div></div>
      <div onClick={onChestClick} ref={ref} className="relative">
        <Picture
          className="block w-[36px] h-[36px]"
          img={chestImages.other.png}
          webp={chestImages.other.webp}
          alt={`chest-for-top-${item}`}
        />
        <div className="absolute w-[16px] h-[16px] right-0 bottom-0">
          <div className="relative w-full h-full">
            <InfoIcon className="w-full h-full relative z-[2] text-main-bg" />
            <div className="absolute rounded-full z-[1] w-[90%] h-[90%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white"></div>
          </div>
        </div>
      </div> */}
      {!!myInfo && (
        <div className="absolute top-0 left-0 overflow-hidden w-full h-full rounded-lg -z-[1]">
          <div className={cn(css.elipse, css['elipse--me'])}></div>
        </div>
      )}
      {positionRewardIsOpen &&
        createPortal(
          <div
            style={{
              top: top - 64,
              right: 0,
            }}
            className={css.infoDetail}
          >
            {[
              ...Object.entries(rewards.wallet),
              ...Object.entries(rewards.lottery || rewards.lotteries || {}),
              ...Object.entries(rewards.materials),
            ].map(([item, quantity]) => (
              <RewardItem
                key={`reward-${item}`}
                item={item}
                quantity={quantity}
              />
            ))}

            <span className={css.polygon}></span>
          </div>,
          document.getElementById('league-rank') as HTMLElement,
        )}
    </div>
  );
};

export { TopItemRequirenments };
